import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import {
    Button,
    Card,
    CardBody,
    Chip,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    IconButton,
    Spinner,
    Typography,
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuildingColumns,
    faEdit,
    faExclamationTriangle,
    faLandmark,
    faLayerGroup,
    faListAlt,
    faPlus,
    faPenToSquare,
    faWallet,
    faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { rootUrl } from '../../../config/config';
import {
    MPESA_WALLET_COPY,
    applyMpesaFormStateChange,
    getMpesaIdentityFieldValue,
    getMpesaLinkedBusinessChild,
    getMpesaWalletPresentation,
    isLockedMpesaParent,
    isValidBusinessParent,
} from './mpesaWalletHelpers';

const defaultFormData = {
    account_name: '',
    account_type: 'INCOME',
    bank_account_number: '',
    bank_name: '',
    mpesa_phone: '',
    mpesa_role: 'PERSONAL',
    mpesa_parent_account_id: '',
    has_pochi: false,
    opening_balance: 0,
    business_opening_balance: 0,
    is_active: true,
    input_by: '',
};

const accountTypeOptions = [
    { value: 'CASH', label: 'Cash' },
    { value: 'MPESA', label: 'M-Pesa' },
    { value: 'BANK', label: 'Bank' },
    { value: 'INCOME', label: 'Income' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'SAVINGS', label: 'Savings' },
];

const mpesaRoleOptions = [
    { value: 'PERSONAL', label: 'PERSONAL' },
    { value: 'BUSINESS', label: 'BUSINESS' },
];

const APP_FONT_FAMILY = "'Nunito', ui-sans-serif, system-ui, sans-serif";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '46px',
        borderRadius: '0.75rem',
        borderColor: state.isFocused ? '#FF5733' : '#cbd5e1',
        boxShadow: 'none',
        fontFamily: APP_FONT_FAMILY,
        fontSize: '0.95rem',
        '&:hover': {
            borderColor: '#FF5733',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 0.85rem',
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontFamily: APP_FONT_FAMILY,
    }),
    singleValue: (base) => ({
        ...base,
        fontFamily: APP_FONT_FAMILY,
        color: '#334155',
    }),
    placeholder: (base) => ({
        ...base,
        fontFamily: APP_FONT_FAMILY,
        color: '#94a3b8',
    }),
    indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: '#e2e8f0',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '0.75rem',
        overflow: 'hidden',
        fontFamily: APP_FONT_FAMILY,
    }),
    option: (base, state) => ({
        ...base,
        fontFamily: APP_FONT_FAMILY,
        backgroundColor: state.isFocused ? '#fff1eb' : '#ffffff',
        color: '#334155',
        cursor: 'pointer',
    }),
};

const buildFormData = (user, account = null, accounts = []) => {
    const linkedBusinessChild = account ? getMpesaLinkedBusinessChild(account, accounts) : null;

    return {
    ...defaultFormData,
    account_name: account?.account_name || defaultFormData.account_name,
    account_type: account?.account_type || defaultFormData.account_type,
    bank_account_number: account?.bank_account_number || defaultFormData.bank_account_number,
    bank_name: account?.bank_name || defaultFormData.bank_name,
    mpesa_phone: account?.mpesa_phone || defaultFormData.mpesa_phone,
    mpesa_role: account?.mpesa_role || defaultFormData.mpesa_role,
    mpesa_parent_account_id: account?.mpesa_parent_account_id ? String(account.mpesa_parent_account_id) : defaultFormData.mpesa_parent_account_id,
    has_pochi: account ? Boolean(account.has_pochi) : defaultFormData.has_pochi,
    opening_balance: account ? Number(account.opening_balance) || 0 : defaultFormData.opening_balance,
    business_opening_balance: linkedBusinessChild ? Number(linkedBusinessChild.opening_balance) || 0 : defaultFormData.business_opening_balance,
    is_active: account ? account.is_active !== false : defaultFormData.is_active,
    input_by: user?.username || defaultFormData.input_by,
    };
};

const CashbookAccounts = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');
    const [showForm] = useState(false);
    const [editingAccount] = useState(null);
    const [formData, setFormData] = useState(buildFormData(user));
    const [errors, setErrors] = useState({});

    const accountLookup = useMemo(() => {
        return accounts.reduce((lookup, account) => {
            lookup[String(account.id)] = account;
            return lookup;
        }, {});
    }, [accounts]);

    const mpesaParentOptions = useMemo(() => {
        return accounts
            .filter((account) => isValidBusinessParent(account, formData.mpesa_phone?.trim() || ''))
            .map((account) => ({
                value: String(account.id),
                label: getMpesaWalletPresentation(account, accountLookup, accounts).optionLabel,
                account,
            }));
    }, [accounts, accountLookup, formData.mpesa_phone]);

    const lockedMpesaParent = isLockedMpesaParent(editingAccount, accounts);

    const updateMpesaFormData = useCallback((updates) => {
        setFormData((prev) => applyMpesaFormStateChange(prev, updates, accounts));
    }, [accounts]);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (response.data?.success) {
                setAccounts(response.data.data || []);
            } else {
                setAccounts(Array.isArray(response.data) ? response.data : []);
            }
            setError('');
        } catch (err) {
            setError('Failed to fetch cashbook accounts.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const handleCreate = () => {
        navigate('/cashbook/accounts/new');
    };

    const handleEdit = (account) => {
        navigate(`/cashbook/accounts/${account.id}/edit`);
    };

    const handleAccountTypeChange = (option) => {
        const nextType = option?.value || '';

        updateMpesaFormData({
            account_type: nextType,
        });
    };

    const handleMpesaRoleChange = (option) => {
        const nextRole = option?.value || 'PERSONAL';

        updateMpesaFormData({
            mpesa_role: nextRole,
        });
    };

    const handleMpesaParentChange = (option) => {
        updateMpesaFormData({
            mpesa_parent_account_id: option?.value || '',
        });
    };

    const handleCancel = () => {
        navigate('/cashbook/accounts');
    };

    const validate = () => {
        const newErrors = {};
        const mpesaPhone = formData.mpesa_phone?.trim() || '';
        if (!formData.account_name?.trim()) newErrors.account_name = 'Account name is required';
        if (!formData.account_type) newErrors.account_type = 'Account type is required';
        if (!['INCOME', 'EXPENSE', 'BANK', 'CASH', 'MPESA', 'SAVINGS'].includes(formData.account_type)) newErrors.account_type = 'Invalid account type';
        if (Number.isNaN(Number(formData.opening_balance))) newErrors.opening_balance = 'Opening balance must be a valid number';

        if (lockedMpesaParent) {
            const lockedIdentityFields = ['account_type', 'mpesa_role', 'mpesa_parent_account_id', 'mpesa_phone', 'has_pochi'];
            const identityChanged = lockedIdentityFields.some((field) => getMpesaIdentityFieldValue(formData, field) !== getMpesaIdentityFieldValue(editingAccount, field));

            if (identityChanged) {
                lockedIdentityFields.forEach((field) => {
                    newErrors[field] = 'This linked PERSONAL wallet has BUSINESS children and its wallet identity cannot be changed.';
                });
            }
        }

        if (formData.account_type === 'MPESA' && !mpesaPhone) {
            newErrors.mpesa_phone = 'M-Pesa phone number is required';
        } else if (mpesaPhone && !/^\d{10,15}$/.test(mpesaPhone)) {
            newErrors.mpesa_phone = 'Phone must be 10–15 digits';
        }

        if (formData.account_type === 'MPESA') {
            if (!['PERSONAL', 'BUSINESS'].includes(formData.mpesa_role)) {
                newErrors.mpesa_role = 'Select a valid M-Pesa wallet role';
            }

            if (formData.mpesa_role === 'BUSINESS') {
                if (!formData.mpesa_parent_account_id) {
                    newErrors.mpesa_parent_account_id = 'Select the linked personal wallet';
                } else {
                    const parentAccount = accounts.find((account) => String(account.id) === String(formData.mpesa_parent_account_id));

                    if (!parentAccount) {
                        newErrors.mpesa_parent_account_id = 'Selected personal wallet was not found';
                    } else if (!isValidBusinessParent(parentAccount, mpesaPhone)) {
                        newErrors.mpesa_parent_account_id = 'Parent wallet must be a PERSONAL M-Pesa account';
                    } else if (editingAccount && String(parentAccount.id) === String(editingAccount.id)) {
                        newErrors.mpesa_parent_account_id = 'An account cannot be its own parent';
                    }
                }
            }

            if (formData.mpesa_role !== 'BUSINESS' && formData.mpesa_parent_account_id) {
                newErrors.mpesa_parent_account_id = 'Only BUSINESS wallet rows can have a parent wallet';
            }

            if (formData.mpesa_role === 'BUSINESS' && formData.has_pochi) {
                newErrors.has_pochi = 'BUSINESS wallet rows cannot enable Pochi';
            }

            if (formData.mpesa_role === 'PERSONAL' && formData.has_pochi && Number.isNaN(Number(formData.business_opening_balance))) {
                newErrors.business_opening_balance = 'Linked business opening balance must be a valid number';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setSaving(true);
        setFormError('');
        setErrors({});

        const payload = {
            ...formData,
            account_name: formData.account_name.trim(),
            bank_name: formData.bank_name?.trim() || '',
            bank_account_number: formData.bank_account_number?.trim() || '',
            mpesa_phone: formData.account_type === 'MPESA' ? formData.mpesa_phone?.trim() || null : null,
            mpesa_role: formData.account_type === 'MPESA' ? formData.mpesa_role || 'PERSONAL' : 'PERSONAL',
            mpesa_parent_account_id: formData.account_type === 'MPESA' && formData.mpesa_role === 'BUSINESS' ? formData.mpesa_parent_account_id || null : null,
            has_pochi: formData.account_type === 'MPESA' ? Boolean(formData.has_pochi) : false,
            opening_balance: Number.parseFloat(formData.opening_balance) || 0,
            business_opening_balance: formData.account_type === 'MPESA' && formData.mpesa_role === 'PERSONAL' && formData.has_pochi
                ? Number.parseFloat(formData.business_opening_balance) || 0
                : 0,
        };

        try {
            if (editingAccount) {
                const response = await axios.patch(
                    `${rootUrl}/api/cashbook/accounts/${editingAccount.id}`,
                    {
                        ...payload,
                        edit_by: user?.username || 'system',
                    },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );

                if (!response.data?.success) {
                    const apiError = new Error(response.data?.message || 'Failed to update account');
                    apiError.apiErrors = response.data?.errors || null;
                    throw apiError;
                }
            } else {
                const response = await axios.post(
                    `${rootUrl}/api/cashbook/accounts`,
                    payload,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );

                if (!response.data?.success) {
                    const apiError = new Error(response.data?.message || 'Failed to create account');
                    apiError.apiErrors = response.data?.errors || null;
                    throw apiError;
                }
            }

            await fetchAccounts();
            handleCancel();
        } catch (err) {
            console.error('Error saving account:', err);
            const apiErrors = err.response?.data?.errors || err.apiErrors || null;
            const apiMessage = err.response?.data?.message || err.message || 'Failed to save account';

            if (apiErrors && typeof apiErrors === 'object') {
                setErrors(apiErrors);
                setFormError(apiMessage === 'Failed to save account' ? 'Please correct the highlighted fields and try again.' : apiMessage);
            } else {
                setFormError(apiMessage);
            }
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) =>
        new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2,
        }).format(Number(amount) || 0);

    const formatDateTime = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const getAccountTypeColor = (type) => {
        const colors = { INCOME: 'green', EXPENSE: 'red', BANK: 'blue', CASH: 'amber', MPESA: 'light-green', SAVINGS: 'teal' };
        return colors[type] || 'gray';
    };

    const summary = useMemo(() => {
        const activeAccounts = accounts.filter((account) => account.is_active !== false).length;
        const bankAccounts = accounts.filter((account) => account.account_type === 'BANK').length;
        const totalBalance = accounts.reduce((sum, account) => sum + (Number(account.current_balance) || 0), 0);

        return {
            totalAccounts: accounts.length,
            activeAccounts,
            bankAccounts,
            totalBalance,
        };
    }, [accounts]);

    const openAccountStatement = useCallback((account) => {
        navigate(`/cashbook/statement/${account.id}`);
    }, [navigate]);

    const openAccountTransactions = useCallback((account) => {
        navigate(`/cashbook/transactions/${account.id}`);
    }, [navigate]);

    const handleRowKeyDown = (event, account) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openAccountStatement(account);
        }
    };

    return (
        <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Cashbook Accounts</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Manage account setup, opening balances, and live cashbook account status.
                    </p>
                </div>
            </div>

            <div className="w-full min-w-0 px-3 sm:px-4 lg:px-6 pb-8 space-y-6">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <Card className="border border-[#f0d9cf] shadow-sm rounded-xl bg-white">
                        <CardBody className="p-5 flex items-start justify-between">
                            <div>
                                <Typography className="text-sm font-semibold text-slate-500">Total Accounts</Typography>
                                <Typography className="text-3xl font-bold text-[#2a2e30] mt-2">{summary.totalAccounts}</Typography>
                            </div>
                            <div className="h-11 w-11 rounded-xl bg-[#fff1eb] text-[#FF5733] flex items-center justify-center">
                                <FontAwesomeIcon icon={faLayerGroup} />
                            </div>
                        </CardBody>
                    </Card>

                    <Card className="border border-[#f0d9cf] shadow-sm rounded-xl bg-white">
                        <CardBody className="p-5 flex items-start justify-between">
                            <div>
                                <Typography className="text-sm font-semibold text-slate-500">Active Accounts</Typography>
                                <Typography className="text-3xl font-bold text-[#2a2e30] mt-2">{summary.activeAccounts}</Typography>
                            </div>
                            <div className="h-11 w-11 rounded-xl bg-[#eefbf3] text-emerald-600 flex items-center justify-center">
                                <FontAwesomeIcon icon={faLandmark} />
                            </div>
                        </CardBody>
                    </Card>

                    <Card className="border border-[#f0d9cf] shadow-sm rounded-xl bg-white">
                        <CardBody className="p-5 flex items-start justify-between">
                            <div>
                                <Typography className="text-sm font-semibold text-slate-500">Combined Balance</Typography>
                                <Typography className="text-2xl font-bold text-[#2a2e30] mt-2">{formatCurrency(summary.totalBalance)}</Typography>
                                <Typography className="text-xs text-slate-500 mt-1">{summary.bankAccounts} bank account(s)</Typography>
                            </div>
                            <div className="h-11 w-11 rounded-xl bg-[#eef4ff] text-blue-600 flex items-center justify-center">
                                <FontAwesomeIcon icon={faWallet} />
                            </div>
                        </CardBody>
                    </Card>
                </div>

                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-4 sm:p-5">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <Typography className="text-base font-bold text-[#2a2e30]">Accounts Register</Typography>
                                <Typography className="text-sm text-slate-500">Review account balances, status, and account details.</Typography>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    onClick={fetchAccounts}
                                    disabled={loading}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'animate-spin' : ''} />
                                    Refresh
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleCreate}
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    New Account
                                </Button>
                            </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                            <Typography className="text-sm text-slate-600">
                                Showing {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
                            </Typography>
                            <Typography className="text-sm text-slate-500">
                                Edit existing bank and cashbook accounts directly from this register
                            </Typography>
                        </div>
                    </CardBody>
                </Card>

                    {error && (
                        <Card className="border border-red-200 bg-red-50 shadow-none rounded-xl">
                        <CardBody className="p-4 flex items-start gap-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5" />
                            <Typography className="text-sm text-red-700">{error}</Typography>
                        </CardBody>
                    </Card>
                )}

                {loading && accounts.length === 0 ? (
                    <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                        <CardBody className="py-12 flex flex-col items-center gap-3">
                            <Spinner className="h-8 w-8 text-[#FF5733]" />
                            <Typography className="text-sm text-slate-600">Loading cashbook accounts...</Typography>
                        </CardBody>
                    </Card>
                ) : accounts.length === 0 ? (
                    <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                        <CardBody className="py-12 text-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-slate-300 mb-3" />
                            <Typography className="text-base font-semibold text-[#2a2e30]">No cashbook accounts found</Typography>
                            <Typography className="text-sm text-slate-500 mt-2">
                                Create a new account to start recording balances and cashbook activity.
                            </Typography>
                        </CardBody>
                    </Card>
                ) : (
                    <>
                        <Card className="hidden md:block min-w-0 border border-[#eadfd8] shadow-sm rounded-xl bg-white overflow-hidden">
                            <CardBody className="p-0">
                                <div className="w-full min-w-0 overflow-x-auto">
                                    <table className="w-full table-fixed text-left">
                                        <thead className="bg-gradient-to-r from-slate-50 to-orange-50/60">
                                            <tr>
                                                <th className="w-[22%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon icon={faBuildingColumns} className="text-[#FF5733]" />
                                                        Account
                                                    </div>
                                                </th>
                                                <th className="w-[12%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">Type</th>
                                                <th className="w-[16%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon icon={faLandmark} className="text-[#FF5733]" />
                                                        Bank
                                                    </div>
                                                </th>
                                                <th className="w-[18%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">
                                                    Opening Balance
                                                </th>
                                                <th className="w-[18%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">
                                                    Current Balance
                                                </th>
                                                <th className="w-[7%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 text-center">Status</th>
                                                <th className="w-[5%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 text-center">Edit</th>
                                                <th className="w-[5%] p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 text-center">Txns</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accounts.map((account) => (
                                                <tr
                                                    key={account.id}
                                                    className="cursor-pointer transition-colors hover:bg-[#fffaf7]"
                                                    onClick={() => openAccountStatement(account)}
                                                    onKeyDown={(event) => handleRowKeyDown(event, account)}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Open statement for ${account.account_name}`}
                                                >
                                                    <td className="p-3 border-b border-slate-100 align-top">
                                                        <div className="space-y-1">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div>
                                                                    <Typography className="text-sm font-semibold text-[#2a2e30] break-words">{account.account_name}</Typography>
                                                                </div>
                                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wide ${account.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                                    <span className={`h-2 w-2 rounded-full ${account.is_active !== false ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                                    {account.is_active !== false ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top">
                                                        <Chip value={account.account_type} color={getAccountTypeColor(account.account_type)} size="sm" className="rounded-sm w-fit" />
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top">
                                                        <div className="space-y-1">
                                                            <Typography className="text-sm text-slate-700 break-words">{account.bank_name || 'Internal account'}</Typography>
                                                            <Typography className="text-xs text-slate-500 break-all flex items-center gap-1">
                                                                {account.bank_account_number || 'No account number'}
                                                            </Typography>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Typography className="text-sm text-slate-700 tabular-nums">{formatCurrency(account.opening_balance)}</Typography>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Typography className="text-sm font-semibold text-[#2a2e30] tabular-nums">{formatCurrency(account.current_balance)}</Typography>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top text-center">
                                                        <span className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${account.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                            {account.is_active !== false ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top text-center">
                                                        <IconButton
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleEdit(account);
                                                            }}
                                                            variant="text"
                                                            size="sm"
                                                            className="h-9 w-9 rounded-full text-slate-600 hover:bg-slate-100 hover:text-[#FF5733] mx-auto"
                                                            aria-label={`Edit ${account.account_name}`}
                                                            title={`Edit ${account.account_name}`}
                                                        >
                                                            <FontAwesomeIcon icon={faPenToSquare} />
                                                        </IconButton>
                                                    </td>
                                                    <td className="p-3 border-b border-slate-100 align-top text-center">
                                                        <IconButton
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                openAccountTransactions(account);
                                                            }}
                                                            variant="text"
                                                            size="sm"
                                                            className="h-9 w-9 rounded-full text-slate-600 hover:bg-slate-100 hover:text-[#FF5733] mx-auto"
                                                            aria-label={`View transactions for ${account.account_name}`}
                                                            title={`View transactions for ${account.account_name}`}
                                                        >
                                                            <FontAwesomeIcon icon={faListAlt} />
                                                        </IconButton>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>

                        <div className="grid grid-cols-1 gap-4 md:hidden">
                            {accounts.map((account) => (
                                <Card
                                    key={account.id}
                                    className="border border-[#eadfd8] shadow-sm rounded-xl bg-white cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md"
                                    onClick={() => openAccountStatement(account)}
                                    onKeyDown={(event) => handleRowKeyDown(event, account)}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Open statement for ${account.account_name}`}
                                >
                                    <CardBody className="p-4 space-y-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <Typography className="text-base font-bold text-[#2a2e30]">{account.account_name}</Typography>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <Chip value={account.account_type} color={getAccountTypeColor(account.account_type)} size="sm" className="rounded-sm" />
                                                </div>
                                            </div>

                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <Typography className="text-xs uppercase tracking-wide text-slate-500">Opening</Typography>
                                                <Typography className="text-sm font-semibold text-[#2a2e30] tabular-nums">{formatCurrency(account.opening_balance)}</Typography>
                                            </div>
                                            <div>
                                                <Typography className="text-xs uppercase tracking-wide text-slate-500">Current</Typography>
                                                <Typography className="text-sm font-semibold text-[#2a2e30] tabular-nums text-right">{formatCurrency(account.current_balance)}</Typography>
                                            </div>
                                            <div>
                                                <Typography className="text-xs uppercase tracking-wide text-slate-500">Account No.</Typography>
                                                <Typography className="text-sm text-slate-700">{account.bank_account_number || '-'}</Typography>
                                            </div>
                                            <div>
                                                <Typography className="text-xs uppercase tracking-wide text-slate-500">Status</Typography>
                                                <Chip value={account.is_active ? 'Active' : 'Inactive'} color={account.is_active ? 'green' : 'gray'} size="sm" className="rounded-sm w-fit" />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                                            <Typography className="text-xs text-slate-500">Updated {formatDateTime(account.edit_date || account.input_date)}</Typography>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openAccountTransactions(account);
                                                    }}
                                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none flex items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faListAlt} />
                                                    Transactions
                                                </Button>
                                                <Button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleEdit(account);
                                                    }}
                                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none flex items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                    Edit Account
                                                </Button>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <Dialog open={showForm} handler={handleCancel} size="md" className="m-2 rounded-xl font-sans overflow-hidden" style={{ fontFamily: APP_FONT_FAMILY }}>
                <DialogHeader className="border-b border-slate-100 text-base font-bold text-[#2a2e30] py-4" style={{ fontFamily: APP_FONT_FAMILY }}>
                    {editingAccount ? 'Edit Cashbook Account' : 'New Cashbook Account'}
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <DialogBody divider className="space-y-5 text-sm py-5 max-h-[70vh] overflow-y-auto" style={{ fontFamily: APP_FONT_FAMILY }}>
                        {formError && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                                <Typography className="text-sm font-medium text-red-700">{formError}</Typography>
                            </div>
                        )}
                        <div className="grid grid-cols-1 gap-5">
                            <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div>
                                    <Typography className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">General</Typography>
                                    <Typography className="text-sm text-slate-500 mt-1">Basic identity and account type.</Typography>
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5 sm:col-span-2">
                                        <Typography className="text-sm font-semibold text-slate-700">Account Name</Typography>
                                        <input
                                            type="text"
                                            value={formData.account_name}
                                            onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                                            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all ${errors.account_name ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'}`}
                                            style={{ fontFamily: APP_FONT_FAMILY }}
                                        />
                                        {errors.account_name && <Typography className="mt-1 text-xs text-red-500">{errors.account_name}</Typography>}
                                    </div>

                                    <div className="space-y-1.5 sm:col-span-2">
                                        <Typography className="text-sm font-semibold text-slate-700">Account Type</Typography>
                                        <Select
                                            value={accountTypeOptions.find((option) => option.value === formData.account_type)}
                                            onChange={handleAccountTypeChange}
                                            options={accountTypeOptions}
                                            styles={selectStyles}
                                            isSearchable={false}
                                            isDisabled={lockedMpesaParent}
                                        />
                                        {errors.account_type && <Typography className="mt-1 text-xs text-red-500">{errors.account_type}</Typography>}
                                    </div>
                                </div>
                            </section>

                            {formData.account_type === 'MPESA' ? (
                                <section className="space-y-4 rounded-2xl border border-amber-100 bg-amber-50/40 p-4 shadow-sm">
                                    <div>
                                        <Typography className="text-xs font-black uppercase tracking-[0.2em] text-amber-600">M-Pesa Wallet Setup</Typography>
                                        <Typography className="text-sm text-amber-900/80 mt-1">Configure the phone, role, and linked Pochi wallet.</Typography>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="space-y-1.5 sm:col-span-2">
                                            <Typography className="text-sm font-semibold text-slate-700">M-Pesa Phone Number</Typography>
                                            <input
                                                type="text"
                                                value={formData.mpesa_phone}
                                                onChange={(e) => updateMpesaFormData({ mpesa_phone: e.target.value })}
                                                placeholder="e.g. 0712345678"
                                                disabled={lockedMpesaParent}
                                                className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all ${errors.mpesa_phone ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'} ${lockedMpesaParent ? 'cursor-not-allowed bg-slate-100' : ''}`}
                                                style={{ fontFamily: APP_FONT_FAMILY }}
                                            />
                                            {errors.mpesa_phone && <Typography className="mt-1 text-xs text-red-500">{errors.mpesa_phone}</Typography>}
                                        </div>

                                        <div className="space-y-1.5 sm:col-span-2">
                                            <Typography className="text-sm font-semibold text-slate-700">Wallet Role</Typography>
                                            <Select
                                                value={mpesaRoleOptions.find((option) => option.value === formData.mpesa_role)}
                                                onChange={handleMpesaRoleChange}
                                                options={mpesaRoleOptions}
                                                styles={selectStyles}
                                                isSearchable={false}
                                                isDisabled={lockedMpesaParent}
                                            />
                                            {errors.mpesa_role && <Typography className="mt-1 text-xs text-red-500">{errors.mpesa_role}</Typography>}
                                        </div>

                                        {formData.mpesa_role === 'PERSONAL' && (
                                            <div className="space-y-2 rounded-xl border border-emerald-100 bg-white px-4 py-3 sm:col-span-2">
                                                <label className="flex items-start gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.has_pochi}
                                                        onChange={(e) => updateMpesaFormData({ has_pochi: e.target.checked })}
                                                        disabled={lockedMpesaParent}
                                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-[#FF5733] focus:ring-[#FF5733]"
                                                    />
                                                    <span className="text-sm text-slate-700">
                                                        <span className="font-semibold">Has Pochi / Business wallet</span>
                                                        <span className="block text-xs text-slate-500 mt-1">
                                                            {MPESA_WALLET_COPY.personalPochiToggle}
                                                        </span>
                                                    </span>
                                                </label>
                                                {errors.has_pochi && <Typography className="text-xs text-red-500">{errors.has_pochi}</Typography>}
                                            </div>
                                        )}

                                        {formData.mpesa_role === 'PERSONAL' && formData.has_pochi && (
                                            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 sm:col-span-2">
                                                <Typography className="text-sm font-semibold text-blue-700">Linked business wallet ready</Typography>
                                                <Typography className="text-xs text-blue-700 mt-1">
                                                    {MPESA_WALLET_COPY.personalReady}
                                                </Typography>
                                            </div>
                                        )}

                                        {formData.mpesa_role === 'BUSINESS' && (
                                            <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:col-span-2">
                                                <Typography className="text-sm font-semibold text-slate-700">Linked PERSONAL Wallet</Typography>
                                                <Select
                                                    value={mpesaParentOptions.find((option) => option.value === formData.mpesa_parent_account_id) || null}
                                                    onChange={handleMpesaParentChange}
                                                    options={mpesaParentOptions}
                                                    styles={selectStyles}
                                                    isSearchable={false}
                                                    placeholder={mpesaParentOptions.length > 0 ? 'Select the personal wallet' : 'No PERSONAL wallet available'}
                                                    isDisabled={mpesaParentOptions.length === 0}
                                                />
                                                <Typography className="text-xs text-slate-500">
                                                    {MPESA_WALLET_COPY.businessParent}
                                                </Typography>
                                                {errors.mpesa_parent_account_id && <Typography className="mt-1 text-xs text-red-500">{errors.mpesa_parent_account_id}</Typography>}
                                            </div>
                                        )}

                                        {lockedMpesaParent && (
                                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:col-span-2">
                                                <Typography className="text-sm font-semibold text-amber-800">Wallet identity locked</Typography>
                                                <Typography className="text-xs text-amber-800 mt-1">
                                                    {MPESA_WALLET_COPY.lockedWallet}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ) : (
                                <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                    <div>
                                        <Typography className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Bank Details</Typography>
                                        <Typography className="text-sm text-slate-500 mt-1">Only shown for non-M-Pesa accounts.</Typography>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="space-y-1.5">
                                            <Typography className="text-sm font-semibold text-slate-700">Bank Name</Typography>
                                            <input
                                                type="text"
                                                value={formData.bank_name}
                                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733]"
                                                style={{ fontFamily: APP_FONT_FAMILY }}
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <Typography className="text-sm font-semibold text-slate-700">Bank Account Number</Typography>
                                            <input
                                                type="text"
                                                value={formData.bank_account_number}
                                                onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733]"
                                                style={{ fontFamily: APP_FONT_FAMILY }}
                                            />
                                        </div>
                                    </div>
                                </section>
                            )}

                            <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div>
                                    <Typography className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Opening Balances</Typography>
                                    <Typography className="text-sm text-slate-500 mt-1">
                                        Set the starting balance for the main wallet and, when enabled, the linked business wallet.
                                    </Typography>
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Typography className="text-sm font-semibold text-slate-700">Primary Opening Balance</Typography>
                                        <input
                                            type="number"
                                            value={formData.opening_balance}
                                            onChange={(e) => updateMpesaFormData({ opening_balance: e.target.value })}
                                            step="0.01"
                                            className={`w-full rounded-xl border bg-white px-4 py-3 text-right text-sm text-slate-700 outline-none transition-all ${errors.opening_balance ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'}`}
                                            style={{ fontFamily: APP_FONT_FAMILY }}
                                        />
                                        {errors.opening_balance && <Typography className="mt-1 text-xs text-red-500">{errors.opening_balance}</Typography>}
                                    </div>

                                    {formData.account_type === 'MPESA' && formData.mpesa_role === 'PERSONAL' && formData.has_pochi ? (
                                        <div className="space-y-1.5">
                                            <Typography className="text-sm font-semibold text-slate-700">Linked Business Opening Balance</Typography>
                                            <input
                                                type="number"
                                                value={formData.business_opening_balance}
                                                onChange={(e) => updateMpesaFormData({ business_opening_balance: e.target.value })}
                                                step="0.01"
                                                className={`w-full rounded-xl border bg-white px-4 py-3 text-right text-sm text-slate-700 outline-none transition-all ${errors.business_opening_balance ? 'border-red-300 focus:border-red-400' : 'border-slate-300 focus:border-[#FF5733]'}`}
                                                style={{ fontFamily: APP_FONT_FAMILY }}
                                            />
                                            <Typography className="text-xs text-slate-500">
                                                {MPESA_WALLET_COPY.businessOpeningBalance}
                                            </Typography>
                                            {errors.business_opening_balance && <Typography className="text-xs text-red-500">{errors.business_opening_balance}</Typography>}
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
                                            <Typography className="text-sm font-semibold text-slate-500">Linked Business Opening Balance</Typography>
                                            <Typography className="text-xs text-slate-500 mt-1">
                                                Shows only when a PERSONAL M-Pesa wallet has Pochi enabled.
                                            </Typography>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 bg-white shadow-sm">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="h-4 w-4 rounded border-slate-300 text-[#FF5733] focus:ring-[#FF5733]"
                                />
                                <span className="text-sm font-medium text-slate-700">Account is active</span>
                            </label>
                        </div>
                    </DialogBody>
                    <DialogFooter className="border-t border-slate-100 gap-2 py-4" style={{ fontFamily: APP_FONT_FAMILY }}>
                        <Button
                            type="button"
                            onClick={handleCancel}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <FontAwesomeIcon icon={faSyncAlt} className="animate-spin" />}
                            {saving ? 'Saving...' : editingAccount ? 'Update Account' : 'Create Account'}
                        </Button>
                    </DialogFooter>
                </form>
            </Dialog>
        </div>
    );
};

export default CashbookAccounts;
